package dev.kotlinfest._2026

import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color
import dev.kotlinfest._2026.SitePalette.nearBackground
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px

/**
 * @property nearBackground A useful color to apply to a container that should differentiate itself from the background
 *   but just a little.
 */
object SitePalette {
    val nearBackground = Color.rgb(0xFFF7FB)
    val cobweb = Color.rgb(0xDCDDDD)
    val brand = Brand()

    class Brand(
        val white: Color = Color.rgb(0xFFFFFF),
        val primary: Color = Color.rgb(0x7F52FF),
        val secondary: Color = Color.rgb(0xC811E2),
        val highlight: Color = Color.rgb(0xE54857),
        val accent: Color = Color.rgb(0xF19D18),
        val ink: Color = Color.rgb(0x1B1B1B),
        val muted: Color = Color.rgb(0x898989),
        val border: Color = Color.rgb(0xDCDDDD),
        val lightGray: Color = Color.rgb(0xCCCCCC),
        val darkGray: Color = Color.rgb(0x707070),
        val softPink: Color = Color.rgb(0xF2C3DB),
        val softLime: Color = Color.rgb(0xDAE497),
        val softBlue: Color = Color.rgb(0x78ACD7),
        val softOrange: Color = Color.rgb(0xF8AB1E),
        val pink: Color = Color.rgb(0xF28FC1),
        val lime: Color = Color.rgb(0xB9C75D),
        val blue: Color = Color.rgb(0x478AC1),
        val orange: Color = Color.rgb(0xE09205),
    )
}

object SessionVenueColors {
    val hallA = SitePalette.brand.softBlue
    val hallB = SitePalette.brand.softLime
    val room402N = SitePalette.brand.softOrange
}

fun sessionVenueColor(venue: String): Color = when (venue) {
    "fun - ホールA" -> SessionVenueColors.hallA
    "val - ホールB" -> SessionVenueColors.hallB
    "?.let - 402N" -> SessionVenueColors.room402N
    else -> SitePalette.brand.lightGray
}

object SiteLayout {
    const val DesktopDesignWidthPx = 1440
    const val DesktopBreakpointPx = 768
    const val DesktopContentWidthPx = 1280
    const val DesktopMarginPx = 80
    const val MobileMarginPx = 24
    const val DesktopGutterPx = 20
    const val MobileGutterPx = 16
}

object SiteLinks {
    const val Sponsors = "/sponsors"
    const val Sponsorship = "/sponsorship"
    const val StaffRecruitment = "/staff-recruitment"
    const val SessionProposal = "https://fortee.jp/kotlin-fest-2026/speaker/proposal/cfp"
    const val X = "https://x.com/kotlin_fest"
    const val YouTube = "https://youtube.com/@japan-kotlin-user-group"
    const val Venue = "https://www.tokyo-cc.co.jp/shinagawa/"
    const val GoogleMap = "https://maps.app.goo.gl/xku1dCgHW5k36BjN8"
}

fun Modifier.maxContentWidth(): Modifier = maxWidth(SiteLayout.DesktopContentWidthPx.px)

fun Modifier.desktopPageHorizontalPadding(minimumPx: Int = 40): Modifier = styleModifier {
    property(
        "padding-left",
        "clamp(${minimumPx}px, 5.56vw, ${SiteLayout.DesktopMarginPx}px)",
    )
    property(
        "padding-right",
        "clamp(${minimumPx}px, 5.56vw, ${SiteLayout.DesktopMarginPx}px)",
    )
}

fun Modifier.fixedPageBackground(): Modifier = styleModifier {
    property("background-image", "url('/background/site-background.svg')")
    property("background-position", "center top")
    property("background-size", "cover")
    property("background-repeat", "no-repeat")
    property("background-attachment", "fixed")
}

fun Modifier.sitePageBackground(): Modifier = backgroundColor(SitePalette.brand.softPink)
    .fixedPageBackground()

fun Modifier.sitePageHero(heightPx: Int): Modifier = fillMaxWidth()
    .height(heightPx.px)
    .overflow(Overflow.Hidden)
    .sitePageBackground()
    .styleModifier { property("position", "relative") }

fun Modifier.kotlinGradientBorder(): Modifier = styleModifier {
    property("border", "2px solid transparent")
    property(
        "background",
        "linear-gradient(#fff, #fff) padding-box, " +
            "linear-gradient(100deg, #7f52ff 0%, #c811e2 55%, #e54857 100%) border-box",
    )
}

fun Modifier.siteCardSurface(): Modifier = backgroundColor(SitePalette.brand.white)
    .border(1.px, LineStyle.Solid, SitePalette.brand.border)
    .borderRadius(1.25.cssRem)
    .boxShadow(0.px, 0.5.cssRem, 1.75.cssRem, color = SitePalette.brand.ink.toRgb().copyf(alpha = 0.05f))

@InitSilk
fun initTheme(ctx: InitSilkContext) {
    ctx.theme.palettes.light.background = Colors.White
    ctx.theme.palettes.light.color = SitePalette.brand.ink
}
